import type { ReactNode } from "react";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`.trim()}>{children}</section>;
}

/** Loading placeholders shaped like the content they replace, so nothing jumps. */
export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="skeleton" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skel-line" style={{ width: `${92 - i * 14}%` }} />
      ))}
    </div>
  );
}

export function PlanBadge({ label, status }: { label: string; status: string | null }) {
  const tone =
    status === "past_due" ? "warn" : status === "canceled" || status === "expired" ? "off" : "on";
  return (
    <span className={`badge badge-${tone}`}>
      {label}
      {status ? <em>{status.replace("_", " ")}</em> : null}
    </span>
  );
}

/** Daily spot allowance. Pro is unlimited, so it renders as a statement, not a bar. */
export function UsageMeter({ used, limit }: { used: number; limit: number | null }) {
  if (limit === null) {
    return (
      <p className="usage-unlimited">
        <strong>{used}</strong> today · unlimited
      </p>
    );
  }
  const pct = Math.min(100, limit === 0 ? 100 : (used / limit) * 100);
  const spent = used >= limit;
  return (
    <div className="usage">
      <div className="usage-row">
        <span>
          <strong>{used}</strong> of {limit} spots today
        </span>
        {spent && <span className="usage-out">limit reached</span>}
      </div>
      <div
        className="meter"
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-label="Daily spots used"
      >
        <div className={`meter-fill${spent ? " meter-full" : ""}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function Notice({ kind = "info", children }: { kind?: "info" | "ok" | "err"; children: ReactNode }) {
  return (
    <p className={`notice notice-${kind}`} role={kind === "err" ? "alert" : "status"}>
      {children}
    </p>
  );
}
